import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getFullUser, setActionTaken } from '../services';
import ExperienceBanner from './banners/experience/experience_banner';
import CitizenshipsLanguagesBanners from './banners/citizenships_languages/citizenships_languages';
import CountryBanner from './banners/country/country_banner';
import EmploymentLocationBanner from './banners/employment_location/employment_location';
// Styles
import styles from './styles.module.scss';

const DataGatheringBannerBootstrap5 = ({ requestedField }) => {
  if (!requestedField) return;

  const [user, setUser] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [fadeOutEffect, setFadeOutEffect] = useState(false);
  const [hidden, setHidden] = useState(false);

  /**
   * When the component did mount, call the services `userFullDetail`
   * which is in charge of getting the user data, if the request is successful
   * set the user data to the variable `user` and the variable `loaded` to `true`.
   */
  useEffect(() => {
    getFullUser()
      .then((response) => response.json())
      .then((data) => {
        setUser(data);
        setLoaded(true);
      })
      .catch(() => {
        setHidden(true);
      });
  }, []);

  /**
   * First, add a fade out opacity effect before removing all DOM
   *
   * @returns {undefined}
   */
  const removeBanner = () => {
    setFadeOutEffect(true);
    setTimeout(() => {
      setHidden(true);
    }, 2000);
  };

  /**
   * If the user clicks in the X symbol at the right top of the banner, sends to the
   * BE the action taken with the services `setActionTaken` and close the banner.
   *
   * @returns {undefined}
   */
  const closeBanner = () => {
    setActionTaken(requestedField, 'closed');
    removeBanner();
  };

  /**
   * Based on the requested field, it returns the appropriate
   * Banner component and pass the user information to it.
   *
   * @param {object} userData - User data
   *
   * @returns {Node} - Banner component
   */
  const displayBanner = (userData) => {
    switch (requestedField) {
      case 'experience_duration':
        return (
          <ExperienceBanner
            userID={userData.id}
            years={userData.experience_duration}
            removeBanner={removeBanner}
          />
        );
      case 'citizenships':
        return (
          <CitizenshipsLanguagesBanners
            type='citizenships'
            userID={userData.id}
            values={userData.citizenships}
            removeBanner={removeBanner}
          />
        );
      case 'languages':
        return (
          <CitizenshipsLanguagesBanners
            type='languages'
            userID={userData.id}
            values={userData.languages}
            removeBanner={removeBanner}
          />
        );
      case 'country':
        return (
          <CountryBanner
            userID={userData.id}
            value={userData.country}
            removeBanner={removeBanner}
          />
        );
      case 'employment_location':
        return (
          <EmploymentLocationBanner
            employments={userData.employments}
            removeBanner={removeBanner}
          />
        );
      // No default
    }
  };

  if (hidden) return null;
  return loaded ? (
    <div
      className={`${styles.dataGatheringWidget} z-depth-2 mb-48 mt-35 ${
        fadeOutEffect ? styles.fadeOut : ''
      }`}
    >
      <div className='text-end'>
        <button
          aria-label='closeBanner'
          type='button'
          className='btn-link btn text-gray-lig'
          onClick={closeBanner}
        >
          <i className='fal icon-times'></i>
        </button>
      </div>
      <div className='container'>
        <div className='row'>
          <div className='col col-lg-9 offset-lg-1'>
            {user && displayBanner(user)}
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div className={styles.loadingPlaceholder} />
  );
};

DataGatheringBannerBootstrap5.propTypes = {
  requestedField: PropTypes.string.isRequired,
};

export default DataGatheringBannerBootstrap5;
